import { QuestionCircleOutlined } from '@ant-design/icons'
import { useChannels } from '@entities/channel'
import { Checkbox } from '@shared/ui/checkbox'
import type { FormProps } from 'antd'
import { Button, Col, Form, Input, Row, Select, Space, Tooltip, Typography } from 'antd'
import axios from 'axios'
import { useEffect } from 'react'
import InputMask from 'react-input-mask'

const { Text } = Typography

type OrganizationType = 'ul' | 'ip' | 'fl'

type OrdFormFields = {
    organization?: string
    type: OrganizationType
    name: string
    mobilePhone?: string
    kpp?: string
    inn: string
    fio?: string
    ogrn: string
    address: string
    isORS: boolean
    isRR: boolean
    rsUrl?: string
}

export const Ord = () => {
    const {
        actions: { editChannel },
        channelDetails
    } = useChannels()

    const [form] = Form.useForm<OrdFormFields>()

    const orgType = Form.useWatch('type', form)
    const isORSChecked = Form.useWatch('IsORS', form)

    useEffect(() => {
        if (!channelDetails?.settings?.ord) return

        const { organization, type, name, mobilePhone, kpp, inn, fio, ogrn, address, isORS, isRR, rsUrl } =
            channelDetails.settings.ord

        form.setFieldsValue({
            organization,
            type: type as OrganizationType,
            inn,
            kpp,
            name,
            fio,
            mobilePhone,
            ogrn,
            address,
            isORS,
            isRR,
            rsUrl
        })
    }, [form, channelDetails])

    const onFinish: FormProps<OrdFormFields>['onFinish'] = async (values) => {
        if (!channelDetails?.id) return

        editChannel({
            id: channelDetails.id,
            data: {
                settings: {
                    ord: {
                        ...values
                    }
                }
            }
        })
    }

    return (
        <Form<OrdFormFields>
            form={form}
            name="add-partner"
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            onFinish={onFinish}
            initialValues={{
                type: 'ul'
            }}
        >
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item
                        label={
                            <Space>
                                <Text>Организация</Text>
                                <Tooltip title="Добавленной организации будет присвоен ID, который будет отправлен ЕРИР.">
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </Space>
                        }
                        name="organization"
                        required={false}
                    >
                        <Input readOnly={true} />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item
                        label="Тип организации"
                        name="type"
                        rules={[{ required: true, message: 'Обязательное поле' }]}
                    >
                        <Select
                            placeholder="Выберите тип"
                            options={[
                                { label: 'Юридическое лицо', value: 'ul' },
                                { label: 'Индивидуальный предприниматель', value: 'ip' },
                                { label: 'Физическое лицо', value: 'fl' }
                            ]}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item
                        label="ИНН"
                        name="inn"
                        validateTrigger="onBlur"
                        validateDebounce={500}
                        rules={[
                            { required: true, message: 'Обязательное поле' },
                            {
                                validator: async (_, value) => {
                                    if (!value) return Promise.resolve()

                                    // защита от гонок: валидируем только актуальное значение
                                    const typed = value

                                    try {
                                        const { data } = await axios.get(`https://egrul.itsoft.ru/${value}.json`)

                                        // если пользователь уже поменял ИНН во время запроса — выходим
                                        if (typed !== value) return Promise.resolve()

                                        // сервер ответил с ошибкой в payload
                                        if (data?.error?.message) {
                                            return Promise.reject(new Error(data.error.message))
                                        }

                                        const ipData = data['СвИП']
                                        const ulData = data['СвЮЛ']

                                        if (ipData) {
                                            const fioAttrs = ipData?.['СвФЛ']?.['ФИОРус']?.['@attributes'] ?? {}
                                            const fio = [fioAttrs['Фамилия'], fioAttrs['Имя'], fioAttrs['Отчество']]
                                                .filter(Boolean)
                                                .join(' ')
                                            const ogrn = ipData?.['@attributes']?.['ОГРНИП'] ?? ''

                                            form.setFieldsValue({ fio, ogrn })
                                        } else if (ulData) {
                                            const ogrn = ulData?.['@attributes']?.['ОГРН'] ?? ''
                                            const kpp = ulData?.['@attributes']?.['КПП'] ?? ''
                                            const name = ulData?.['СвНаимЮЛ']?.['@attributes']?.['НаимЮЛПолн'] ?? ''

                                            form.setFieldsValue({ name, ogrn, kpp })
                                        }

                                        return Promise.resolve()
                                    } catch (err) {
                                        // если это ответ сервера с ошибкой — валим валидацию с его message
                                        if (axios.isAxiosError(err) && err.response?.data?.error?.message) {
                                            return Promise.reject(new Error('Запись с таким ИНН не найдена'))
                                        }
                                        // сетевой/прочий фейл без ответа сервера — пропускаем валидацию
                                        return Promise.resolve()
                                    }
                                }
                            }
                        ]}
                    >
                        <Input placeholder="Введите ИНН" />
                    </Form.Item>
                </Col>
            </Row>

            {orgType === 'ul' && (
                <>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="КПП" name="kpp">
                                <Input placeholder="Введите КПП" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="ОПФ и полное наименование компании" name="name">
                                <Input placeholder="Введите ОФП" />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            )}

            {(orgType === 'ip' || orgType === 'fl') && (
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item label="ФИО" name="fio" rules={[{ required: true, message: 'Обязательное поле' }]}>
                            <Input placeholder="Введите ФИО" />
                        </Form.Item>
                    </Col>
                </Row>
            )}

            {(orgType === 'ip' || orgType === 'ul') && (
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item label="ОГРН" name="ogrn">
                            <Input placeholder="Введите огрн" />
                        </Form.Item>
                    </Col>
                </Row>
            )}

            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item label="Адрес" name="address" rules={[{ required: true, message: 'Обязательное поле' }]}>
                        <Input placeholder="Введите адрес" />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item label="Абонентский номер мобильного телефона" name="mobilePhone">
                        <InputMask mask="+7(999)999-99-99" maskChar={null}>
                            {(inputProps) => <Input {...inputProps} placeholder="Введите номер телефона" />}
                        </InputMask>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={4}>
                    <Form.Item name="IsORS" valuePropName="checked">
                        <Checkbox>Является ОРС</Checkbox>
                    </Form.Item>
                </Col>
                <Col span={4}>
                    <Form.Item name="isRR" valuePropName="checked">
                        <Checkbox>Является РР</Checkbox>
                    </Form.Item>
                </Col>
            </Row>

            {isORSChecked && (
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            label="URL рекламной системы"
                            name="orsUrl"
                            rules={[{ required: true, message: 'Введите URL' }]}
                        >
                            <Input placeholder="https://..." />
                        </Form.Item>
                    </Col>
                </Row>
            )}

            <Row gutter={16}>
                <Col span={4}>
                    <Button type="primary" htmlType="submit">
                        Сохранить изменения
                    </Button>
                </Col>
            </Row>
        </Form>
    )
}
